import { useCallback } from 'react';
import { DefaultValues, Mode, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDialogStore } from '@spendbrain/common/stores';
import { ZodType } from 'zod';

export interface UseBaseDialogFormProps<
  TValues,
  TDefaultValues,
  TValidation,
  TReturnValues,
> {
  onClose?: () => void;
  defaultValues?: DefaultValues<TDefaultValues>;
  validationSchema: TValidation;
  onSubmit: (values: TValues) => Promise<void> | Promise<TReturnValues> | void;
  mode?: Mode;
}

export const useBaseDialogForm = <
  TValues extends Record<string, unknown>,
  TDefaultValues extends Record<string, unknown>,
  TValidation extends ZodType,
  TReturnValues,
>({
  onClose,
  defaultValues,
  validationSchema,
  onSubmit: onSubmitExternal,
  mode = 'onChange',
}: UseBaseDialogFormProps<
  TValues,
  TDefaultValues,
  TValidation,
  TReturnValues
>) => {
  const { close: closeDialog, setClosable } = useDialogStore();

  const formProps = useForm<TDefaultValues, null, TValues>({
    defaultValues,
    mode,
    reValidateMode: 'onChange',
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = useCallback(
    async (values: TValues) => {
      setClosable(false);
      await onSubmitExternal(values);
    },
    [onSubmitExternal, setClosable]
  );

  const onFormClose = useCallback(() => {
    onClose?.();
    closeDialog();
    formProps.reset();
  }, [onClose, formProps, closeDialog]);

  return {
    formProps,
    onSubmit,
    onFormClose,
  };
};
